// Polish public holidays (święta ustawowe)
// Fixed dates and movable holidays (Easter-dependent)

export interface PolishHoliday {
  date: string // YYYY-MM-DD
  name: string
}

// Calculate Easter Sunday using Anonymous Gregorian algorithm
function getEasterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

// Add days to a date
function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

// Format date as YYYY-MM-DD
function formatDate(date: Date): string {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")
  return `${year}-${month}-${day}`
}

// Get all Polish public holidays for a given year
export function getPolishHolidays(year: number): PolishHoliday[] {
  const easter = getEasterSunday(year)
  
  const holidays: PolishHoliday[] = [
    // Fixed holidays
    { date: `${year}-01-01`, name: "Nowy Rok" },
    { date: `${year}-01-06`, name: "Trzech Króli" },
    { date: `${year}-05-01`, name: "Święto Pracy" },
    { date: `${year}-05-03`, name: "Święto Konstytucji" },
    { date: `${year}-08-15`, name: "Wniebowzięcie NMP" },
    { date: `${year}-11-01`, name: "Wszystkich Świętych" },
    { date: `${year}-11-11`, name: "Święto Niepodległości" },
    { date: `${year}-12-25`, name: "Boże Narodzenie" },
    { date: `${year}-12-26`, name: "Drugi dzień Świąt" },
    
    // Easter-dependent movable holidays
    { date: formatDate(easter), name: "Wielkanoc" },
    { date: formatDate(addDays(easter, 1)), name: "Poniedziałek Wielkanocny" },
    { date: formatDate(addDays(easter, 49)), name: "Zielone Świątki" }, // Pentecost
    { date: formatDate(addDays(easter, 60)), name: "Boże Ciało" }, // Corpus Christi
  ]
  
  // Wigilia became a public holiday from 2025 onwards
  if (year >= 2025) {
    holidays.push({ date: `${year}-12-24`, name: "Wigilia" })
  }
  
  return holidays
}

// Get holidays for a specific month
export function getPolishHolidaysForMonth(year: number, month: number): Map<string, string> {
  const holidays = getPolishHolidays(year)
  const monthStr = month.toString().padStart(2, "0")
  const prefix = `${year}-${monthStr}`
  
  const result = new Map<string, string>()
  for (const holiday of holidays) {
    if (holiday.date.startsWith(prefix)) {
      result.set(holiday.date, holiday.name)
    }
  }
  return result
}

// Check if a date is a Polish public holiday
export function isPolishHoliday(dateStr: string): boolean {
  const year = parseInt(dateStr.slice(0, 4), 10)
  const holidays = getPolishHolidays(year)
  return holidays.some(h => h.date === dateStr)
}

// Get holiday name for a date (or null if not a holiday)
export function getPolishHolidayName(dateStr: string): string | null {
  const year = parseInt(dateStr.slice(0, 4), 10)
  const holidays = getPolishHolidays(year)
  const holiday = holidays.find(h => h.date === dateStr)
  return holiday?.name ?? null
}
